import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { toast } from "react-hot-toast";
import { baseAxios } from "../../utils/config";
import { formatMoney } from "../../utils/appointmentDeposits";
import { colors } from "../../theme/colors";
import {
  CONTROL_RADIUS,
  dialogCloseIconSx,
  dialogPaperSx,
  dialogTitleSx,
  fieldSx,
  primaryBtnSx,
  secondaryBtnSx,
} from "./schedulerDialogStyles";

const AddDepositModal = ({ open, event, onClose, onRecorded }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [details, setDetails] = useState(null);
  const [amountInput, setAmountInput] = useState("");

  useEffect(() => {
    if (!open || !event?.appointmentId) return undefined;
    let cancelled = false;
    setAmountInput("");
    setDetails(null);
    setLoading(true);

    baseAxios
      .get(`/scheduler/deposits/${event.appointmentId}`)
      .then((res) => {
        if (!cancelled) setDetails(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(
          err?.response?.data?.message || "Could not load appointment details.",
          { duration: 3500 }
        );
        onClose?.();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, event?.appointmentId]);

  const services = details?.services?.length
    ? details.services
    : (event?.uniqueServices || []).map((s) => ({
        serviceId: s.service_id,
        serviceName: s.service_name || s.title,
        price: Number(s.servicePrice || 0),
      }));

  const appointmentTotal = useMemo(() => {
    if (details?.appointmentTotal != null) {
      return Number(details.appointmentTotal);
    }
    return services.reduce((sum, s) => sum + Number(s.price || 0), 0);
  }, [details, services]);

  const existingDeposits = details?.deposits || event?.deposits || [];
  const remainingTotal =
    details?.remainingTotal != null
      ? Number(details.remainingTotal)
      : Math.max(
          0,
          appointmentTotal -
            existingDeposits.reduce((sum, d) => sum + Number(d.amount || 0), 0)
        );

  const parsedAmount = Number(
    String(amountInput).replace(/[^0-9.]/g, "")
  );
  const amountValid =
    Number.isFinite(parsedAmount) &&
    parsedAmount > 0 &&
    parsedAmount <= remainingTotal + 0.001;

  const handleAmountChange = (value) => {
    const cleaned = value.replace(/[^0-9.]/g, "");
    if ((cleaned.match(/\./g) || []).length > 1) return;
    const parts = cleaned.split(".");
    if (parts[1] && parts[1].length > 2) return;
    setAmountInput(cleaned);
  };

  const handleSubmit = async () => {
    if (!amountValid || saving) return;
    setSaving(true);
    try {
      const res = await baseAxios.post("/scheduler/deposits", {
        appointmentId: event.appointmentId,
        amount: parsedAmount,
      });
      if (res.status === 200) {
        toast.success("Deposit recorded", { duration: 2500 });
        onRecorded?.(res.data);
        onClose?.();
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Could not record the deposit.",
        { duration: 4000 }
      );
    } finally {
      setSaving(false);
    }
  };

  const start = details?.start
    ? dayjs(details.start)
    : event?.start
      ? dayjs(event.start)
      : null;

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose?.()}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography sx={{ fontSize: "1.1rem", fontWeight: 700 }} color="inherit">
          Add Deposit
        </Typography>
        <IconButton
          disabled={saving}
          onClick={onClose}
          sx={dialogCloseIconSx}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        {loading ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress size={28} />
          </Stack>
        ) : (
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Client
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                {details?.clientName || event?.title || "Appointment"}
              </Typography>
            </Box>
            <Stack direction="row" justifyContent="space-between" gap={2}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">
                  Stylist
                </Typography>
                <Typography variant="body2" noWrap>
                  {details?.stylistName || event?.user_name || "—"}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 0, textAlign: "right" }}>
                <Typography variant="caption" color="text.secondary">
                  When
                </Typography>
                <Typography variant="body2">
                  {start ? start.format("ddd, MMM D · h:mm A") : "—"}
                </Typography>
              </Box>
            </Stack>
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                borderRadius: CONTROL_RADIUS,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, display: "block", mb: 0.75 }}
              >
                Services
              </Typography>
              <Stack spacing={0.5}>
                {services.map((service, index) => (
                  <Stack
                    key={`${service.serviceId || service.service_id}-${index}`}
                    direction="row"
                    justifyContent="space-between"
                    gap={1}
                  >
                    <Typography variant="body2">
                      {service.serviceName || service.service_name}
                    </Typography>
                    <Typography variant="body2">
                      {formatMoney(service.price)}
                    </Typography>
                  </Stack>
                ))}
                {existingDeposits.map((deposit) => (
                  <Stack
                    key={deposit.id}
                    direction="row"
                    justifyContent="space-between"
                    gap={1}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {deposit.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      −{formatMoney(deposit.amount)}
                    </Typography>
                  </Stack>
                ))}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ borderTop: "1px dashed", borderColor: "divider", pt: 0.75 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Appointment total
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatMoney(appointmentTotal)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Remaining</Typography>
                  <Typography variant="body2">
                    {formatMoney(remainingTotal)}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Enter Deposit amount"
              value={amountInput}
              onChange={(e) => handleAmountChange(e.target.value)}
              placeholder="0.00"
              disabled={saving || remainingTotal <= 0}
              inputProps={{ inputMode: "decimal" }}
              sx={fieldSx}
            />
            <Typography
              variant="caption"
              sx={{ color: colors.goldHover, fontWeight: 600 }}
            >
              Security deposits are cash only
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2 }}>
        <Button disabled={saving} onClick={onClose} sx={secondaryBtnSx}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={saving || loading || !amountValid}
          onClick={handleSubmit}
          startIcon={
            saving ? <CircularProgress size={16} color="inherit" /> : null
          }
          sx={primaryBtnSx}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddDepositModal;
